// Base Interfaces
interface BaseSearch {
  id?: string;
  slug: string;
  query: string;
  progressStatus?: SearchProgress;
  results: SearchResult[];
}

export interface LLMModel {
  value: string;
  label: string;
}

// Search Result Related Types
export interface SearchCriteria {
  criteria: string;
  isMatch: string;
  reason: string;
}

export interface SearchSource {
  title?: string;
  url?: string;
  snippet?: string;
}

export interface SearchResult {
  // Identity
  id: string;
  name: string;
  firstName: string;
  lastName: string;

  // Company Information
  companyName: string;
  companyWebsite: string;
  domain: string;

  // Title Information
  title: string;
  titleFirstName: string;
  titleLastName: string;
  titleMatch: string;
  titleReason: string;
  titleCriteriaAndReasons: unknown[];

  // Source Information
  linkedinURL: string;
  pageURL?: string; // to be removed
  pageTitle?: string; // to be removed
  pageSnippet?: string; // to be removed
  sources?: SearchSource[];

  // Match Information
  match: string;
  reason: string;
  additionalSearch: string;
  criteriaAndReasons: SearchCriteria[];
  formattedCriteriaAndReasons?: string;

  // Metrics
  llmCost: number;

  // DB
  candidateID?: string;
  sourceID?: string;

  // Contact data
  email?: string;
  phone?: string; // not used yet
  profilePic?: string;
}

// Store Related Types
export interface SearchInfo extends BaseSearch {
  // Error Handling
  error: string | null;

  // Metrics
  timeToFirstMatch: number | null;

  // Search Configuration
  criteria: string[];
  keywords: string[] | KeywordsDomain[];
  category: string;
  model: LLMModel;

  status?: string; // "visible"
  searchStatus?: string; // "created", "processing", "expanding", "completed"
  progressStatus?: SearchProgress;

  // Search Metadata
  definiteMatch: number | null;
  partialMatch: number | null;
  totalResults: number | null;
  findMore?: boolean;

  // Extras TODO: Sort out and remove
  numPages?: number;
  definiteMatchCount?: string;
  totalResultsFound?: number;
  totalResultsGenerated?: number;
  totalLLMCost?: number;
  mode?: string;

  isPublic?: boolean;
  userID?: string;
  algoVersion?: string;
}

// API Related Types
export interface Search extends BaseSearch {
  id?: string;
  matchesCount?: number;
  createdAt?: Date;
  updatedAt?: Date;
  userID?: string;
  slug: string;
  query: string;
  progressStatus?: SearchProgress;
  status?: string;
  category?: string;
  definiteMatch?: number;
  partialMatch?: number;
  totalResults?: number;
  numPages?: number;
  criteria?: string[];
  keywords?: string[];
  model?: LLMModel;
  mode?: string;
  error?: string;
  isPublic: boolean;
}
export interface KeywordsDomain {
  keywords: string;
  domain: string;
}

export interface QueryMetadataResponse {
  criteria: string[];
  keywords: string[];
  searchType: string;
  numPages?: number;
  definiteMatchCount?: string;
  model: LLMModel;
}

export interface CriteriaV3_5 {
  about: string;
  displayShort: string;
  fullDescription: string;
  isCritical: boolean;
}

export interface QueryMetadataResponseV3_5 {
  criteria: CriteriaV3_5[];
  keywords: KeywordsDomain[];
  searchType: string;
  searchOrder: string;
  numPages?: number;
  model?: string;
}

export interface SearchMetadataResponse {
  searchID: number;
  searchQuery: string;
  criteria: string[];
  category: string;
  keywords: string[];
  userID: number;
  mode: string;
  createdAt: string;
  updatedAt: string;
  searchSlug: string;
  numPages: number;
  status: string;
  totalResults: number;
  definiteMatch: number;
  partialMatch: number;
}

export interface SearchMetadata {
  id?: string;
  keywords?: string[];
  criteria?: string[];
  numPages?: number;
  partialMatch?: number;
  definiteMatch?: number;
  matchesCount?: number;
  createdAt?: Date;
  updatedAt?: Date;
  userID?: string;
  slug: string;
  query: string;
  category?: string;
  mode?: string;
  status?: string;
  searchStatus?: string;
  isPublic: boolean;
  algoVersion?: string;
}

export interface SearchOptions {
  mode?: "pro" | "default";
  customCriteria?: string[];
  redirectAfterCreate?: boolean;
  findAll?: boolean;
}

export interface SearchParams {
  query: string;
  criteria?: string[] | CriteriaV3_5[];
  keywords?: string[] | KeywordsDomain[];
  category?: string;
  model?: string;
  mode?: string;
  userID?: string;
  pageNum?: number;
  definiteMatchCount?: string;
  searchOrder?: string;
}

export interface ExpandSearchParams {
  search_id: string;
  definiteMatchCount: string;
}

export interface LLMModel {
  value: string;
  label: string;
}

export interface GroupedSearches {
  [key: string]: SearchMetadata[];
}

export interface SearchHistoryRecord {
  status?: string;
  searchStatus?: string;
  progressStatus?: SearchProgress; //local
  matchesCount?: number;
  slug?: string;
  id?: string;
}

// Constants
export const SEARCH_PROGRESS = {
  NOT_STARTED: "notStarted",
  IN_PROGRESS: "inProgress",
  FINISHED: "finished",
  CANCELLED: "cancelled",
  UNDEFINED: undefined,
} as const;

export const MATCH_STATUS = {
  DEFINITE_MATCH: "definiteMatch",
  PARTIAL_MATCH: "partialMatch",
  NOT_MATCH: "notMatch",
  NEED_MORE_INFO: "needMoreInfo",
} as const;

export const SUBSCRIPTION_TIER = {
  FREE: "free",
  PLUS: "plus",
  PRO: "pro",
  TEAM: "team",
  MAX: "team", // alias,
} as const;

export const CREDITS_SUBSCRIPTION_TIER = {
  BRONZE: "credits_bronze",
  SILVER: "credits_silver",
  GOLD: "credits_gold",
} as const;

export type SearchStatus = "visible" | "invisible";

// locally tracked search progress status, not returned from the API
export type SearchProgress = "notStarted" | "inProgress" | "finished" | "cancelled" | undefined;

export type MatchStatus = "definiteMatch" | "partialMatch" | "notMatch" | "needMoreInfo";
